import { Title } from '@solidjs/meta'
import { useParams } from '@solidjs/router'
import { type Component, createResource, createSignal, Show } from 'solid-js'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import PageRenderer from '~/layers/PageRenderer'
import { normalizeContent, pageService } from '~/services/page.service'
import { resolveError } from '~/utils/error'

/**
 * Página pública creada con el constructor. La abre cualquiera con el enlace,
 * sin sesión. Todo lo que pinta el contenido vive en PageRenderer, el mismo que
 * usa el lienzo del editor.
 */
const PublicBuilderPage: Component = () => {
  const params = useParams()

  /**
   * El título vive en una señal y no en el recurso: solid-meta relee los hijos
   * de <Title> al desmontar, cuando el recurso ya está desechado.
   */
  const [documentTitle, setDocumentTitle] = createSignal('Florez Jaramillo')

  const [page] = createResource(
    () => params.slug,
    async (slug: string) => {
      try {
        const data = await pageService.getPublic(slug)
        const content = normalizeContent(data.nodes)
        setDocumentTitle(`${content.settings.title || data.name} | Florez Jaramillo`)
        return { data, content }
      } catch {
        return null
      }
    }
  )

  return (
    <>
      <Title>{documentTitle()}</Title>

      <div class="min-h-dvh bg-surface">
        <Show when={page.loading}>
          <div class="py-24 text-center text-foreground-subtle">Cargando...</div>
        </Show>

        <Show when={page.error}>
          <Container class="max-w-2xl py-16">
            <Card>
              <Card.Body>
                <div class="py-12 text-center">
                  <Icon icon="material-symbols-light:link-off" size={48} class="mx-auto mb-3 text-foreground-subtle" />
                  <h1 class="text-lg font-semibold text-foreground">Página no disponible</h1>
                  <p class="text-sm text-foreground-subtle mt-1">{resolveError(page.error)}</p>
                </div>
              </Card.Body>
            </Card>
          </Container>
        </Show>

        <Show when={page() && !page.error}>
          <PageRenderer content={page()!.content} interactive />
        </Show>
      </div>
    </>
  )
}

export default PublicBuilderPage
